import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { BehaviorSubject, combineLatest, map, Observable, of, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Contact } from '../../../../../contacts/models/contacts';
import { OrgDepartmentAssignment } from '../../../../models/organisation-detail';
import { IsActiveComponent } from '../../../../../contacts/components/is-active/is-active.component';
import { AvatarComponent } from '../../../../../../../../components/avatar/avatar.component';
import { AssignableParty } from '../../../../../contacts/components/add-department-contact/models/assignable-party';
import { TransportModeIconPipe } from '../../../../../../../../pipes/transport-mode-icon.pipe';
import {
  AddDepartmentContactComponent,
  AddDepartmentContactData,
  AddDepartmentContactResult
} from '../../../../../contacts/components/add-department-contact/add-department-contact.component';

const CONTACT_DIALOG_CHROME = { panelClass: 'contact-dialog-panel', backdropClass: 'contact-dialog-backdrop' };

// Below this, the inline "add department" panel shows the full unassigned list rather than
// filtering — mirrors AddPersonContactComponent's own MIN_SEARCH_LENGTH convention.
const MIN_SEARCH_LENGTH = 3;

@Component({
  selector: 'app-departments-section',
  standalone: true,
  imports: [
    AsyncPipe,
    NgOptimizedImage,
    ReactiveFormsModule,
    MatIcon,
    CdkDrag,
    CdkDropList,
    CdkDragPlaceholder,
    IsActiveComponent,
    AvatarComponent,
    TransportModeIconPipe
  ],
  templateUrl: './departments-section.component.html',
  styleUrl: './departments-section.component.scss'
})
export class DepartmentsSectionComponent implements OnInit, OnChanges {
  @Input() departments: OrgDepartmentAssignment[] = [];
  // Full contact list fetched once by the parent (organisation-detail) and shared with
  // PeopleSectionComponent — filtered to `type === 'Department'` below.
  @Input() contacts: Contact[] = [];
  @Input() organisationParty: AssignableParty | null = null;
  // Keyed by Product id, resolved once by the parent (see OrganisationDetailComponent) — needed
  // to set OrgDepartmentAssignment.product (a name) when adding an unassigned contact here, since
  // Contact only carries productId. Same lookups the Contacts grid resolves its own icon against.
  @Input() productsById: Map<number, string> = new Map();
  @Output() add = new EventEmitter<OrgDepartmentAssignment>();
  @Output() remove = new EventEmitter<number>();
  @Output() reorder = new EventEmitter<OrgDepartmentAssignment[]>();

  readonly searchControl = new FormControl('', { nonNullable: true });

  addOpen = false;
  filteredContacts$: Observable<Contact[]> = of([]);

  private contacts$ = new BehaviorSubject<Contact[]>([]);
  // Nudged from ngOnChanges whenever `departments` changes, so filteredContacts$ recomputes its
  // already-assigned exclusion set right away — combineLatest only reacts to contacts$ and the
  // search box on their own, and `departments` is a plain @Input, not a stream, so without this a
  // contact just added/removed here would keep showing (or stay hidden) in the dropdown until the
  // admin typed something to make the search box re-emit.
  private assignmentsChanged$ = new BehaviorSubject<void>(undefined);
  private matDialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.filteredContacts$ = combineLatest([
      this.contacts$,
      this.searchControl.valueChanges.pipe(startWith('')),
      this.assignmentsChanged$
    ]).pipe(
      map(([contacts, term]) => {
        const assigned = new Set(this.departments.map(department => department.contactId));
        const unassigned = contacts.filter(contact => contact.type === 'Department' && !assigned.has(contact.id));
        const value = term.trim().toLowerCase();
        if (value.length < MIN_SEARCH_LENGTH) {
          return unassigned;
        }
        return unassigned.filter(contact => contact.displayName.toLowerCase().includes(value));
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    // No autoOpenAdd for Departments — the new-organisation flow only pre-opens the People panel
    // (organisations-design-spec.md §1.2), matching the Add-organisation user journey.
    if (changes['departments']) {
      this.assignmentsChanged$.next();
    }
    if (changes['contacts']) {
      this.contacts$.next(this.contacts);
    }
  }

  trackByContactId(_: number, contact: Contact) {
    return contact.id;
  }

  trackByAssignmentId(_: number, assignment: OrgDepartmentAssignment) {
    return assignment.contactId;
  }

  drop({ previousIndex, currentIndex }: CdkDragDrop<OrgDepartmentAssignment[]>) {
    const reordered = [...this.departments];
    moveItemInArray(reordered, previousIndex, currentIndex);
    this.reorder.emit(reordered);
  }

  selectContact(contact: Contact) {
    this.add.emit({
      contactId: contact.id,
      displayName: contact.displayName,
      description: contact.description,
      address: null,
      phone: contact.phone,
      mobilePhone: contact.mobilePhone,
      email: contact.email,
      isActive: contact.isActive,
      sortOrder: this.departments.length,
      product: contact.productId !== null ? (this.productsById.get(contact.productId) ?? null) : null
    });
    this.addOpen = false;
  }

  createNewContact() {
    this.matDialog
      .open<AddDepartmentContactComponent, AddDepartmentContactData, AddDepartmentContactResult>(
        AddDepartmentContactComponent,
        {
          ...CONTACT_DIALOG_CHROME,
          data: {
            existingNames: this.contacts.map(contact => contact.displayName),
            preselectedParty: this.organisationParty ?? undefined
          }
        }
      )
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result?.action === 'Added') {
          const contact = result.contact;
          this.add.emit({
            contactId: Number(contact.id),
            displayName: contact.displayName,
            description: null,
            address: contact.address,
            phone: contact.phone,
            mobilePhone: contact.mobilePhone,
            email: contact.email,
            isActive: contact.isActive,
            sortOrder: this.departments.length,
            // ContactDetails (the dialog's "Added" result) doesn't carry productId/product —
            // falls back to the initials avatar until the page is reloaded, same gap
            // ContactsComponent has for a freshly-created row (see its loadContacts() comment).
            product: null
          });
          this.addOpen = false;
        }
      });
  }
}
